import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import Header from 'components/Header';
import { useAppSelector } from 'core/store/hooks';
import { setPromoCode } from 'core/store/userSlice';
import useNetworkStatus from 'hooks/useNetworkStatus';
import DevelopersPage from 'pages/DevelopersPage';
import FAQ from 'pages/FAQPage';
import HomePage from 'pages/HomePage';
import LandingPage from 'pages/LandingPage';
import NoInternetPage from 'pages/NoInternetPage';
import OnboardingPage from 'pages/OnboardingPage';
import PageNotFound from 'pages/PageNotFound';
import PlaygroundV2Page from 'pages/PlaygroundV2Page';
import PostPaymentPage from 'pages/PostPaymentPage';
import PricingPage from 'pages/PricingPage';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LIGHT_GREY } from 'utils/theme';
import ExpandedNavbar from './NavigationBar/ExpandedNavbar';
import NavBar from './NavigationBar/NavBar';

const bodyWidth = 1000;

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  useNetworkStatus();

  const navigate = useNavigate();

  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const isOnline = useAppSelector((state) => state.network.isOnline);
  const isNavbarExpanded = useAppSelector((state) => state.navbar.isNavbarExpanded);
  const navbarWidth = useAppSelector((state) => state.navbar.navbarWidth);
  const isHomePage = location.pathname === '/home';

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const promoCode = queryParams.get('promo_code');

    if (promoCode) {
      dispatch(setPromoCode(promoCode));
      navigate('/pricing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, dispatch]);

  const ProtectedRoute = ({ isAllowed, children, returnTo }) => {
    if (!isAllowed) {
      loginWithRedirect({
        appState: {
          returnTo: returnTo,
        },
      }).catch((error) => {
        console.error('Error with loginWithRedirect:', error);
      });
    }

    return children ? children : <PageNotFound />;
  };

  if (!isOnline) {
    return (
      <Grid
        container
        direction="column"
        minWidth={isMobile ? 'auto' : '1200px'}
        width={isMobile ? 'auto' : '100vw'}
        margin="auto"
      >
        <NoInternetPage />
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'auto',
        minHeight: '100vh',
        flexDirection: isHomePage
          ? { mobile: 'column', tablet: 'column', desktop: 'column' }
          : { mobile: 'column', tablet: 'column', desktop: 'row' },
        bgcolor: LIGHT_GREY,
        paddingX: isHomePage
          ? { mobile: 0, tablet: 0, desktop: '80px' }
          : { mobile: 0, tablet: 0, desktop: 0 },
      }}
    >
      {/* Header */}
      {isHomePage ? (
        <Header />
      ) : (
        <Box
          sx={{
            width: {
              mobile: '100%',
              tablet: '100%',
              desktop: `${navbarWidth}px`,
            },
            top: 0,
            zIndex: 1000,
            display: 'flex',
            justifyContent: {
              mobile: 'center',
              tablet: 'center',
              desktop: 'flex-start',
            },
            transition: 'width 0.3s ease',
          }}
        >
          <NavBar />
          {isNavbarExpanded && <ExpandedNavbar />}
        </Box>
      )}
      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: {
              mobile: '100%',
              tablet: '100%',
              desktop: isHomePage ? '100%' : `${bodyWidth}px`,
            },
            boxSizing: 'border-box',
            padding: isHomePage
              ? { mobile: '0px 16px', tablet: '0px 24px 24px', desktop: 0 }
              : {
                  mobile: '16px',
                  tablet: '16px 24px 24px',
                  desktop: '32px',
                },
          }}
        >
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/postpayment" element={<PostPaymentPage />} />
            <Route path="/developers" element={<DevelopersPage />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute isAllowed={isAuthenticated} returnTo="/home">
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute isAllowed={isAuthenticated} returnTo="onboarding">
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playgroundV2"
              element={
                <ProtectedRoute isAllowed={isAuthenticated} returnTo="/playgroundV2">
                  <PlaygroundV2Page />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default App;
